import React from 'react';

import type {
  ICardDaCarteira,
  ICompetenciaDoCard,
  IEmpresaInativa,
  Situacao,
} from '../../../../../hooks/useFechamentoContabilMensal';
import {
  ABREV_MES,
  corDaBarra,
  ETAPAS_DO_CARD,
  receitaCurta,
  situacaoDaTela,
} from '../regrasDaCarteira';
import { Check } from './icones';

/** "set" ou, com vários anos, "set/26". */
const mesDoCard = (competencia: string, comAno: boolean) => {
  const [a, m] = competencia.split('-');
  const ab = ABREV_MES[Number(m) - 1].toLowerCase();
  return comAno ? `${ab}/${a.slice(2)}` : ab;
};

const ARTIGO: React.CSSProperties = {
  minWidth: 0,
  boxSizing: 'border-box',
  background: '#FFFFFF',
  border: '1px solid #E4E4E7',
  borderRadius: 12,
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 9,
};

const CHIP: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: 11,
  whiteSpace: 'nowrap',
};

function Cabeca({ nome, linha1, linha2 }: { nome: string; linha1: string; linha2: string }) {
  return (
    <div style={{ minWidth: 0, paddingRight: 18 }}>
      <div
        style={{
          fontSize: 12.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={nome}
      >
        {nome}
      </div>
      <div
        style={{
          fontSize: 11,
          color: '#64748B',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={linha1}
      >
        {linha1}
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: '#94A3B8',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={linha2}
      >
        {linha2}
      </div>
    </div>
  );
}

function Barras({ cores }: { cores: string[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 6,
        marginTop: 'auto',
      }}
    >
      {ETAPAS_DO_CARD.map((e, j) => (
        <span
          key={e.chave}
          style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}
        >
          <span style={{ height: 4, borderRadius: 2, background: cores[j] }} />
          <span style={{ fontSize: 10, color: '#64748B' }}>{e.nome}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Com vários meses no card, os meses ocupam o lugar da trilha de etapas, no mesmo
 * formato: barra na cor da situação do mês e o mês embaixo. Clicar escolhe o mês.
 */
function Meses({
  meses,
  marcadas,
  onSelecionar,
  onAbrir,
}: {
  meses: ICompetenciaDoCard[];
  marcadas: string[];
  onSelecionar: (competencia: string) => void;
  onAbrir: (competencia: string) => void;
}) {
  const emOrdem = [...meses].reverse();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${emOrdem.length}, minmax(0, 1fr))`,
        gap: emOrdem.length > 6 ? 3 : 6,
        marginTop: 'auto',
      }}
    >
      {emOrdem.map(m => {
        const sm = situacaoDaTela(m.situacao);
        const marcada = marcadas.includes(m.competencia);
        return (
          <button
            type="button"
            key={m.competencia}
            className="empMes"
            onClick={() => onSelecionar(m.competencia)}
            onDoubleClick={() => onAbrir(m.competencia)}
            aria-pressed={marcada}
            title={`${m.competencia.slice(5, 7)}/${m.competencia.slice(0, 4)} · ${sm.nome}${
              m.com_pendencia ? ' · com pendência' : ''
            }`}
          >
            <span
              style={{
                display: 'block',
                height: marcada ? 6 : 4,
                borderRadius: 2,
                background: sm.cor,
              }}
            />
            <span
              style={{
                display: 'block',
                fontSize: 10,
                color: marcada ? '#1B6FD0' : '#64748B',
                fontWeight: marcada ? 700 : 400,
              }}
            >
              {mesDoCard(m.competencia, false)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function CartaoDaEmpresa({
  linha,
  grupoFixo,
  variosAnos,
  selecionado,
  marcadas,
  onSelecionar,
  onAbrir,
}: {
  linha: ICardDaCarteira;
  /** situação escolhida no filtro: a etiqueta mostra o detalhe dela */
  grupoFixo: Situacao | null;
  variosAnos: boolean;
  selecionado: boolean;
  /** competências do card que estão no detalhe abaixo */
  marcadas: string[];
  /** clique simples: a competência entra ou sai do detalhe abaixo */
  onSelecionar: (competencia: string) => void;
  /** dois cliques: a tela completa da empresa na competência */
  onAbrir: (competencia: string) => void;
}) {
  const ref = linha.referencia;
  const varios = linha.competencias.length > 1;
  const s = situacaoDaTela(ref.situacao);
  // com a situação fixa, a etiqueta é o detalhe (só fechadas e liberadas têm um único)
  const sub = s.subs.find(x => ref.subs.includes(x.chave));
  const temTag = grupoFixo ? ref.situacao === 'fech' || ref.situacao === 'lib' : true;
  const tag = grupoFixo ? (sub?.rotulo ?? s.curto) : s.curto;
  const cores = ETAPAS_DO_CARD.map(e => corDaBarra(ref.etapas?.[e.chave]?.estado, ref.situacao));
  return (
    <article
      className={`empCard${selecionado ? ' sel' : ''}`}
      style={{ ...ARTIGO, borderLeft: `3px solid ${s.cor}` }}
    >
      <button
        type="button"
        className="empAbrir"
        onClick={() => onSelecionar(ref.competencia)}
        onDoubleClick={() => onAbrir(ref.competencia)}
        title="Clique para ver os detalhes · dois cliques para abrir a tela completa"
        aria-pressed={selecionado}
        aria-label={`${selecionado ? 'Tirar da seleção' : 'Ver detalhes de'} ${linha.razao}`}
      />
      {selecionado && (
        <span className="empCheck" aria-hidden="true">
          <Check tam={12} cor="#FFFFFF" traco={3.4} />
        </span>
      )}
      <Cabeca
        nome={linha.razao}
        linha1={`${linha.external_id} · ${linha.regime} · receita ${receitaCurta(linha.receita)}`}
        linha2={`${linha.unidade} · ${linha.responsavel}`}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
        <span style={{ ...CHIP, fontWeight: 700, background: s.texto, color: '#FFFFFF' }}>
          {varios
            ? `${mesDoCard(linha.competencias[linha.competencias.length - 1].competencia, variosAnos)}–${mesDoCard(ref.competencia, variosAnos)}`
            : mesDoCard(ref.competencia, variosAnos)}
        </span>
        {!varios && temTag && (
          <span style={{ ...CHIP, fontWeight: 600, background: s.suave, color: s.texto }}>
            {tag}
          </span>
        )}
        {linha.sistema_proprio && (
          <span
            style={{ ...CHIP, fontWeight: 600, background: '#E9F4FD', color: '#155FB0' }}
            title={`Fecha em sistema próprio (${linha.sistema_proprio}): aqui é só controle`}
          >
            Sistema próprio
          </span>
        )}
        {linha.competencias.some(c => c.com_pendencia) && (
          <span
            style={{ ...CHIP, fontWeight: 600, background: '#FCF1DD', color: '#B45309' }}
            title="Encerrada no Domínio com pendência aberta na revisão"
          >
            {varios
              ? `Fechada com pendência · ${linha.competencias.filter(c => c.com_pendencia).length}`
              : 'Fechada com pendência'}
          </span>
        )}
      </div>
      {/* com vários meses no card a trilha de etapas sai: ela é de um mês só */}
      {varios ? (
        <Meses
          meses={linha.competencias}
          marcadas={marcadas}
          onSelecionar={onSelecionar}
          onAbrir={onAbrir}
        />
      ) : (
        <Barras cores={cores} />
      )}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, fontWeight: 700 }}
      >
        <span style={{ color: linha.criticas ? '#DC2626' : '#64748B' }}>
          {linha.criticas} crít.
        </span>
        <span style={{ color: linha.altas ? '#B45309' : '#64748B' }}>{linha.altas} altas</span>
        <span style={{ marginLeft: 'auto', fontWeight: 500, color: '#52525B' }}>
          {linha.competencias.length === 1 ? '1 mês' : `${linha.competencias.length} meses`}
        </span>
      </div>
    </article>
  );
}

const CINZA = '#94A3B8';

/** O card só informa: quem saiu no mês tem a revisão na carteira; a inativa sem data
 * de saída não tem revisão (o cadastro precisa dizer até quando foi cliente). */
export function CartaoInativa({ e, variosAnos }: { e: IEmpresaInativa; variosAnos: boolean }) {
  const mmaa = (c: string | null) => (c ? `${c.slice(5, 7)}/${c.slice(2, 4)}` : '');
  const saiu = e.motivo === 'saida';
  const tag = saiu ? 'Saiu no mês' : 'Inativa sem data de saída';
  const rodape = saiu ? `cliente até ${mmaa(e.cliente_ate)}` : 'corrigir o cadastro';
  const situacao = saiu ? 'revisada na carteira' : 'fora da revisão';
  return (
    <article className="empCard" style={{ ...ARTIGO, borderLeft: `3px solid ${CINZA}` }}>
      <Cabeca
        nome={e.razao}
        linha1={`${e.external_id} · ${e.regime} · ${situacao}`}
        linha2={`${e.unidade} · ${e.responsavel}`}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
        <span style={{ ...CHIP, fontWeight: 700, background: '#64748B', color: '#FFFFFF' }}>
          {mesDoCard(e.competencia, variosAnos)}
        </span>
        <span style={{ ...CHIP, fontWeight: 600, background: '#EEF2F6', color: '#64748B' }}>
          {tag}
        </span>
      </div>
      <Barras cores={[CINZA, CINZA, CINZA, CINZA]} />
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, fontWeight: 700 }}
      >
        <span style={{ color: '#64748B' }}>0 crít.</span>
        <span style={{ color: '#64748B' }}>0 altas</span>
        <span style={{ marginLeft: 'auto', fontWeight: 500, color: '#52525B' }}>{rodape}</span>
      </div>
    </article>
  );
}
